// utils/performance.ts — performance-утилиты для «Упряжка» V1-core.
// KISS: ленивая загрузка модулей + мемоизация для тяжёлых селекторов.

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Lazy-load обёртка с preloading hint.
 * Используется в router для code-splitting страниц.
 *
 * @example
 *   const TasksPage = lazyLoad(() => import('@/pages/tasks'));
 *   <Suspense fallback={<LoadingSpinner />}><TasksPage /></Suspense>
 */
export function lazyLoad<T extends ComponentType<unknown>>(
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> & { preload: () => Promise<unknown> } {
  const Component = lazy(importer);
  // Augment LazyExoticComponent with preload для Link-hover-prefetch.
  const augmented = Component as LazyExoticComponent<T> & { preload: () => Promise<unknown> };
  augmented.preload = importer;
  return augmented;
}

/**
 * Shallow-equal для объектов (1 уровень). Возвращает true, если
 * все enumerable-свойства shallow-равны.
 */
export function shallowEqual<T extends Record<string, unknown>>(a: T, b: T): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

/**
 * Debounce для keyboard input / search-as-you-type.
 * Возвращает обёртку, вызывающую fn не чаще чем раз в `ms` миллисекунд.
 */
export function debounce<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  ms: number,
): (...args: TArgs) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: TArgs) => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}
